import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/auth-store'
import { useShopStore } from '../stores/shop-store'
import { useUserStore } from '../stores/user-store'
import { crispService, type CrispUser } from '../services/crisp'

/**
 * Composable for managing Crisp chat integration with shop data
 */
export function useCrisp() {
  const authStore = useAuthStore()
  const shopStore = useShopStore()
  const userStore = useUserStore()

  // Computed property to check if we have the necessary data for Crisp setup
  const canSetupCrisp = computed(() => {
    return !!(shopStore.shopDetails?.crisp_id && userStore.userName && userStore.email)
  })

  // Computed Crisp user data from shop details
  const crispUser = computed<CrispUser>(() => ({
    nickname: `${userStore.userName} (ID: ${shopStore.shopDetails?.id})`,
    email: userStore.email,
    company: `${userStore.shopName} • ${userStore.domain}`,
    website: userStore.domain ? `https://${userStore.domain}` : undefined,
  }))

  // Computed session data with shop information
  const sessionData = computed(() => {
    const s = shopStore.shopDetails
    return {
      shop_id: s?.id,
      shop_name: userStore.shopName,
      shop_domain: userStore.domain,
      shopify_domain: s?.myshopify_domain,
      full_domain_url: userStore.domain ? `https://${userStore.domain}` : undefined,
      shopify_url: s?.myshopify_domain ? `https://${s.myshopify_domain}` : undefined,
      plan_name: userStore.planName,
      plan_code: s?.plan_name,
      install_status: userStore.installStatus,
      install_date: s?.install_date,
      created_at: s?.created_at,
      country: userStore.country,
      country_code: s?.country_code,
      currency: userStore.currency,
      timezone: userStore.timezone,
      iana_timezone: s?.iana_timezone,
      weight_unit: s?.weight_unit,
      scopes: userStore.scopes.join(','),
      scope_count: userStore.scopes.length,
      crisp_session_id: s?.crisp_id,
    }
  })

  // Computed segments based on shop characteristics
  const userSegments = computed(() => {
    const segments: string[] = []

    // Add plan-based segment
    if (userStore.planName) {
      segments.push(`plan:${userStore.planName.toLowerCase().replace(/\s+/g, '_')}`)
    }

    // Add install status segment
    if (userStore.installStatus) {
      segments.push(`status:${userStore.installStatus}`)
    }

    // Add country segment
    if (userStore.country) {
      segments.push(`country:${userStore.country.toLowerCase().replace(/\s+/g, '_')}`)
    }

    // Add currency segment
    if (userStore.currency) {
      segments.push(`currency:${userStore.currency}`)
    }

    // Add shop ID segment for easy searching
    if (shopStore.shopDetails?.id) {
      segments.push(`shop_id:${shopStore.shopDetails.id}`)
    }

    // Add scope-based segments
    if (userStore.scopes.includes('write_pixels')) {
      segments.push('has_pixel_access')
    }
    if (userStore.scopes.includes('read_orders')) {
      segments.push('has_order_access')
    }

    return segments
  })

  /**
   * Setup Crisp session with shop data
   */
  const setupCrispSession = async () => {
    if (!canSetupCrisp.value) {
      console.log('Crisp: Cannot setup session - missing required data')
      return
    }

    const sessionId = shopStore.shopDetails!.crisp_id

    await crispService.setupShopSession({
      sessionId,
      user: crispUser.value,
      sessionData: sessionData.value,
      segments: userSegments.value,
    })

    console.log('Crisp: Shop session setup completed', {
      sessionId,
      user: crispUser.value,
      segments: userSegments.value,
    })
  }

  /**
   * Clear Crisp session
   */
  const clearCrispSession = async () => {
    await crispService.clearSession()
    console.log('Crisp: Session cleared')
  }

  /**
   * Update Crisp session data when shop details change
   */
  const updateCrispSession = async () => {
    if (!canSetupCrisp.value) return

    // Update user info
    await crispService.setUser(crispUser.value)

    // Update session data
    await crispService.setSessionData(sessionData.value)

    // Update segments
    await crispService.setSegments(userSegments.value)

    console.log('Crisp: Session updated with latest shop data')
  }

  // Watch for changes in shop details and update Crisp accordingly
  const stopWatcher = watch(
    [() => shopStore.shopDetails, () => authStore.isAuthenticated],
    ([shopDetails, isAuthenticated]) => {
      if (isAuthenticated && shopDetails?.crisp_id) {
        // Setup or update session when authenticated with shop data
        setupCrispSession()
      } else {
        // Clear session when not authenticated
        clearCrispSession()
      }
    },
    { immediate: true },
  )

  // Watch for changes in user/shop data and update session
  const stopDataWatcher = watch(
    [crispUser, sessionData, userSegments],
    () => {
      if (canSetupCrisp.value) {
        updateCrispSession()
      }
    },
    { deep: true },
  )

  // Setup Crisp when component mounts
  onMounted(() => {
    if (canSetupCrisp.value) {
      setupCrispSession()
    }
  })

  // Cleanup watchers when component unmounts
  onUnmounted(() => {
    stopWatcher()
    stopDataWatcher()
  })

  return {
    // State
    canSetupCrisp,
    crispUser,
    sessionData,
    userSegments,

    // Actions
    setupCrispSession,
    clearCrispSession,
    updateCrispSession,

    // Crisp service methods
    showChat: () => crispService.show(),
    hideChat: () => crispService.hide(),
    openChat: () => crispService.open(),
    closeChat: () => crispService.close(),
  }
}
